import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { TriangleAlertIcon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { useTheme } from "simplydo/react/theme";
import { Card } from "simplydo/react/ui/card";
import { Icon } from "simplydo/react/ui/icon";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

import { Sticky } from "components/lib/UI";

const CHALLENGE_CLOSING_WARNING_MS = 60 * 60 * 1000;

/** Evaluated at call time — do not memoize over Date.now(). */
const isWithinChallengeClosingWarning = (
  deadline: string | Date | null | undefined,
): boolean => {
  if (!deadline) {
    return false;
  }
  const msUntilDeadline = new Date(deadline).getTime() - Date.now();
  return msUntilDeadline > 0 && msUntilDeadline <= CHALLENGE_CLOSING_WARNING_MS;
};

type ChallengeClosingWarningProps = {
  isEditing?: boolean;
  isSubmitted?: boolean;
  challenge?: { stage?: string; deadline?: string | Date | null } | null;
};

const ChallengeClosingWarning = ({
  isEditing,
  isSubmitted,
  challenge,
}: ChallengeClosingWarningProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();

  const challengeIsClosed = challenge?.stage === "closed";
  const challengeDeadline = challenge?.deadline;
  const shouldShow =
    !!isEditing &&
    !isSubmitted &&
    !challengeIsClosed &&
    isWithinChallengeClosingWarning(challengeDeadline);

  if (!shouldShow) {
    return null;
  }

  const stickyTop = location.pathname.startsWith("/ideas/")
    ? theme.sizes.isMobile
      ? 140
      : 155
    : 5;

  return (
    <Sticky top={stickyTop} style={{ zIndex: 80, marginBottom: 16 }}>
      <Card fluid>
        <Message color="orange">
          <Icon icon={TriangleAlertIcon} />
          <MessageHeader>
            {t("ideas.deadline.challengeClosing.header")}
          </MessageHeader>
          <MessageContent>
            {t("ideas.deadline.challengeClosing.banner", {
              time: formatDate(challengeDeadline, "HH:mm"),
            })}
          </MessageContent>
        </Message>
      </Card>
    </Sticky>
  );
};

export default ChallengeClosingWarning;
